import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import Select from "react-select";
import {
  createGeneralDesignApproval,
  deleteGeneralDesignApproval,
  getGeneralDesignAllContracts,
  getGeneralDesignApproval,
  getGeneralDesignUserReports,
  getGeneralDesignUsersWithReports,
  updateGeneralDesignCorrection,
} from "../../../../api/paymentsProgressApi";
import { getUsersSummary } from "../../../../api/api";
import { selectStyles } from "../../../../utils/selectStyles";
import { UserContext } from "../../../../contexts/CurrentUserContext";
import GeneralDesignTable from "./GeneralDesignTable";

const GeneralDesignSection = ({
  isAdms,
  isReadOnly = false,
  month,
  year,
  savingId,
  setSavingId,
  setConfirmModal,
  setCommentModal,
  formatMoney,
}) => {
  const { userData } = useContext(UserContext);

  const [generalDesignRows, setGeneralDesignRows] = useState([]);
  const [generalDesignLoading, setGeneralDesignLoading] = useState(false);
  const [generalDesignError, setGeneralDesignError] = useState(null);
  const [expandedGeneralProjects, setExpandedGeneralProjects] = useState(new Set());
  const [expandedGeneralUsers, setExpandedGeneralUsers] = useState(new Set());
  const [generalUsersByContract, setGeneralUsersByContract] = useState({});
  const [generalReportsByKey, setGeneralReportsByKey] = useState({});
  const [loadingGeneralUsers, setLoadingGeneralUsers] = useState({});
  const [loadingGeneralReports, setLoadingGeneralReports] = useState({});

  const [sendApprovalModalOpen, setSendApprovalModalOpen] = useState(false);
  const [sendingApproval, setSendingApproval] = useState(false);
  const [approverOptions, setApproverOptions] = useState([]);
  const [selectedSection, setSelectedSection] = useState(null);
  const [selectedApprovers, setSelectedApprovers] = useState([]);
  const [approvalBySectionId, setApprovalBySectionId] = useState({});
  const [deletingApproval, setDeletingApproval] = useState(false);
  const [generalDesignExpanded, setGeneralDesignExpanded] = useState(true);
  const [sectionTotalsState, setSectionTotalsState] = useState({});

  const isSectionOnApproval = (contractSectionsId) =>
    Boolean(approvalBySectionId[contractSectionsId]?.id);

  const canDeleteApprovalForSection = (contractSectionsId) => {
    const approval = approvalBySectionId[contractSectionsId];
    if (!approval?.id) return false;
    const creatorId = approval?.creator_id ?? approval?.creator?.id ?? null;
    return creatorId != null && String(creatorId) === String(userData?.id);
  };

  const deleteApprovalForSection = async (contractSectionsId, projectLabel) => {
    if (isReadOnly) return;
    const approval = approvalBySectionId[contractSectionsId];
    if (!approval?.id) return;
    const ok = window.confirm(
      `Удалить согласование для «${projectLabel || "выбранного раздела"}»?`,
    );
    if (!ok) return;
    try {
      setDeletingApproval(true);
      await deleteGeneralDesignApproval(
        Number(contractSectionsId),
        Number(year),
        Number(month),
      );
      setApprovalBySectionId((prev) => {
        const next = { ...prev };
        delete next[contractSectionsId];
        return next;
      });
      alert("Согласование удалено");
    } catch (err) {
      alert(err?.message || "Ошибка удаления согласования Генплан РП");
    } finally {
      setDeletingApproval(false);
    }
  };

  const generalUsersByContractRef = useRef(generalUsersByContract);
  const loadingGeneralUsersRef = useRef(loadingGeneralUsers);
  const generalReportsByKeyRef = useRef(generalReportsByKey);
  const loadingGeneralReportsRef = useRef(loadingGeneralReports);

  useEffect(() => {
    generalUsersByContractRef.current = generalUsersByContract;
  }, [generalUsersByContract]);

  useEffect(() => {
    loadingGeneralUsersRef.current = loadingGeneralUsers;
  }, [loadingGeneralUsers]);

  useEffect(() => {
    generalReportsByKeyRef.current = generalReportsByKey;
  }, [generalReportsByKey]);

  useEffect(() => {
    loadingGeneralReportsRef.current = loadingGeneralReports;
  }, [loadingGeneralReports]);

  const toggleGeneralProject = (contractSectionsId) => {
    setExpandedGeneralProjects((prev) => {
      const next = new Set(prev);
      if (next.has(contractSectionsId)) next.delete(contractSectionsId);
      else next.add(contractSectionsId);
      return next;
    });
  };

  const toggleGeneralUser = (contractSectionsId, userId) => {
    const key = `${contractSectionsId}-${userId}`;
    setExpandedGeneralUsers((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  useEffect(() => {
    if (!isAdms) {
      setGeneralDesignRows([]);
      setGeneralDesignLoading(false);
      setGeneralDesignError(null);
      return;
    }

    let cancelled = false;
    setGeneralDesignLoading(true);
    setGeneralDesignError(null);
    setExpandedGeneralProjects(new Set());
    setExpandedGeneralUsers(new Set());
    setGeneralUsersByContract({});
    setGeneralReportsByKey({});
    setLoadingGeneralUsers({});
    setLoadingGeneralReports({});
    setApprovalBySectionId({});
    setSectionTotalsState({});
    getGeneralDesignAllContracts(month, year)
      .then(async (data) => {
        if (cancelled) return;
        const rows = (Array.isArray(data) ? data : []).map((row) => ({
          ...row,
          correction: row?.correction || {},
        }));
        setGeneralDesignRows(rows);

        const approvalsFromRows = {};
        rows.forEach((row) => {
          const id = Number(row?.contract_sections_id);
          const approval = row?.approval ?? row?.general_design_approval ?? null;
          if (!Number.isNaN(id) && approval && approval.id) {
            approvalsFromRows[id] = approval;
          }
        });
        if (Object.keys(approvalsFromRows).length > 0) {
          setApprovalBySectionId((prev) => ({ ...prev, ...approvalsFromRows }));
        }

        const idsToFetch = rows
          .map((row) => Number(row?.contract_sections_id))
          .filter((id) => Number.isFinite(id) && !approvalsFromRows[id]);

        const CONCURRENCY = 6;
        for (let i = 0; i < idsToFetch.length; i += CONCURRENCY) {
          if (cancelled) return;
          const chunk = idsToFetch.slice(i, i + CONCURRENCY);
          const results = await Promise.all(
            chunk.map(async (id) => {
              try {
                const approval = await getGeneralDesignApproval(
                  Number(year),
                  Number(month),
                  id,
                );
                return [id, approval];
              } catch {
                return [id, null];
              }
            }),
          );
          if (cancelled) return;
          const update = {};
          results.forEach(([id, approval]) => {
            if (approval && approval.id) update[id] = approval;
          });
          if (Object.keys(update).length > 0) {
            setApprovalBySectionId((prev) => ({ ...prev, ...update }));
          }
        }
      })
      .catch((err) => {
        if (cancelled) return;
        setGeneralDesignRows([]);
        setGeneralDesignError(err?.message || "Ошибка загрузки Генплан РП");
      })
      .finally(() => {
        if (!cancelled) setGeneralDesignLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [isAdms, month, year]);

  useEffect(() => {
    if (!isAdms || expandedGeneralProjects.size === 0) return;
    let cancelled = false;
    const contractIdsToLoad = Array.from(expandedGeneralProjects).filter(
      (contractId) =>
        generalUsersByContractRef.current[contractId] === undefined &&
        !loadingGeneralUsersRef.current[contractId],
    );

    contractIdsToLoad.forEach((contractId) => {
      setLoadingGeneralUsers((prev) => ({ ...prev, [contractId]: true }));
      getGeneralDesignUsersWithReports(month, year, contractId)
        .then((data) => {
          if (cancelled) return;
          setGeneralUsersByContract((prev) => ({
            ...prev,
            [contractId]: Array.isArray(data) ? data : [],
          }));
        })
        .catch(() => {
          if (cancelled) return;
          setGeneralUsersByContract((prev) => ({ ...prev, [contractId]: [] }));
        })
        .finally(() => {
          setLoadingGeneralUsers((prev) => ({ ...prev, [contractId]: false }));
        });
    });

    return () => {
      cancelled = true;
    };
  }, [isAdms, month, year, expandedGeneralProjects]);

  useEffect(() => {
    if (!isAdms || expandedGeneralUsers.size === 0) return;
    let cancelled = false;
    const keysToLoad = Array.from(expandedGeneralUsers).filter(
      (key) =>
        generalReportsByKeyRef.current[key] === undefined &&
        !loadingGeneralReportsRef.current[key],
    );

    keysToLoad.forEach((key) => {
      const [contractSectionsIdRaw, userIdRaw] = key.split("-");
      const contractSectionsId = Number(contractSectionsIdRaw);
      const userId = Number(userIdRaw);
      if (Number.isNaN(contractSectionsId) || Number.isNaN(userId)) return;

      setLoadingGeneralReports((prev) => ({ ...prev, [key]: true }));
      getGeneralDesignUserReports(month, year, contractSectionsId, userId)
        .then((data) => {
          if (cancelled) return;
          const reports = Array.isArray(data)
            ? data
            : Array.isArray(data?.reports)
              ? data.reports
              : [];
          const correction = data && !Array.isArray(data) ? (data.correction || {}) : {};
          setGeneralReportsByKey((prev) => ({
            ...prev,
            [key]: {
              correction,
              reports: Array.isArray(reports) ? reports : [],
            },
          }));
        })
        .catch(() => {
          if (cancelled) return;
          setGeneralReportsByKey((prev) => ({ ...prev, [key]: { correction: {}, reports: [] } }));
        })
        .finally(() => {
          setLoadingGeneralReports((prev) => ({ ...prev, [key]: false }));
        });
    });

    return () => {
      cancelled = true;
    };
  }, [isAdms, month, year, expandedGeneralUsers]);

  useEffect(() => {
    if (!isAdms || generalDesignRows.length === 0) return;
    let cancelled = false;

    const ids = generalDesignRows
      .map((row) => Number(row?.contract_sections_id))
      .filter((id) => Number.isFinite(id));

    const loadOneSection = async (contractSectionsId) => {
      setSectionTotalsState((prev) => ({ ...prev, [contractSectionsId]: "loading" }));
      try {
        let users = generalUsersByContractRef.current[contractSectionsId];
        if (!Array.isArray(users)) {
          const data = await getGeneralDesignUsersWithReports(
            month,
            year,
            contractSectionsId,
          );
          if (cancelled) return;
          users = Array.isArray(data) ? data : [];
          setGeneralUsersByContract((prev) => ({
            ...prev,
            [contractSectionsId]: users,
          }));
        }

        await Promise.all(
          users.map(async (user) => {
            const key = `${contractSectionsId}-${user.id}`;
            if (generalReportsByKeyRef.current[key] !== undefined) return;
            try {
              const fetched = await getGeneralDesignUserReports(
                month,
                year,
                contractSectionsId,
                user.id,
              );
              if (cancelled) return;
              const reports = Array.isArray(fetched)
                ? fetched
                : Array.isArray(fetched?.reports)
                  ? fetched.reports
                  : [];
              const correction =
                fetched && !Array.isArray(fetched) ? fetched.correction || {} : {};
              setGeneralReportsByKey((prev) => ({
                ...prev,
                [key]: { correction, reports },
              }));
            } catch {
              if (cancelled) return;
              setGeneralReportsByKey((prev) => ({
                ...prev,
                [key]: { correction: {}, reports: [] },
              }));
            }
          }),
        );

        if (cancelled) return;
        setSectionTotalsState((prev) => ({ ...prev, [contractSectionsId]: "ready" }));
      } catch {
        if (cancelled) return;
        setSectionTotalsState((prev) => ({ ...prev, [contractSectionsId]: "error" }));
      }
    };

    const SECTION_CONCURRENCY = 3;
    const run = async () => {
      for (let i = 0; i < ids.length; i += SECTION_CONCURRENCY) {
        if (cancelled) return;
        const chunk = ids.slice(i, i + SECTION_CONCURRENCY);
        await Promise.all(chunk.map(loadOneSection));
      }
    };
    run();

    return () => {
      cancelled = true;
    };
  }, [isAdms, generalDesignRows, month, year]);

  const sectionOptions = useMemo(
    () =>
      generalDesignRows
        .map((row) => {
          const id = Number(row?.contract_sections_id);
          if (!id || Number.isNaN(id)) return null;
          if (approvalBySectionId[id]?.id) return null;
          const projectName = row?.project?.name || `ID ${row?.project_id || "—"}`;
          const sectionName = row?.section?.section?.name || "—";
          return {
            value: id,
            label: `${projectName} — ${sectionName}`,
          };
        })
        .filter(Boolean),
    [generalDesignRows, approvalBySectionId],
  );

  const openSendApprovalModal = async () => {
    if (isReadOnly) return;
    try {
      const usersSummary = await getUsersSummary({ isActive: true, pageSize: 1000 });
      const approvers = (usersSummary?.users || []).map((u) => ({
        value: u.id,
        label: [u.surname, u.name].filter(Boolean).join(" ").trim() || `ID ${u.id}`,
      }));
      setApproverOptions(approvers);
      setSelectedSection(null);
      setSelectedApprovers([]);
      setSendApprovalModalOpen(true);
    } catch (err) {
      alert(err?.message || "Не удалось загрузить данные для согласования");
    }
  };

  const submitSendApproval = async () => {
    if (isReadOnly) return;
    if (!selectedSection?.value) {
      alert("Выберите раздел проекта для согласования");
      return;
    }
    const approverIds = selectedApprovers.map((a) => a.value);
    if (approverIds.length === 0) {
      alert("Выберите хотя бы одного согласующего");
      return;
    }
    try {
      setSendingApproval(true);
      const created = await createGeneralDesignApproval(
        Number(selectedSection.value),
        Number(year),
        Number(month),
        approverIds,
      );
      if (created && created.id) {
        setApprovalBySectionId((prev) => ({
          ...prev,
          [Number(selectedSection.value)]: created,
        }));
      }
      alert("Раздел Генплан РП успешно отправлен на согласование");
      setSendApprovalModalOpen(false);
    } catch (err) {
      alert(err?.message || "Ошибка отправки на согласование Генплан РП");
    } finally {
      setSendingApproval(false);
    }
  };

  const sectionsWithoutApprovalCount = useMemo(
    () =>
      generalDesignRows.filter(
        (row) => !approvalBySectionId[Number(row?.contract_sections_id)]?.id,
      ).length,
    [generalDesignRows, approvalBySectionId],
  );

  if (!isAdms) return null;

  return (
    <>
      <div className="pp-table-wrap">
        <table className="pp-table">
          <thead>
            <tr
              className="pp-user-row"
              onClick={() => setGeneralDesignExpanded((v) => !v)}
            >
              <th>
                <span
                  className={`pp-expand ${generalDesignExpanded ? "expanded" : ""}`}
                  aria-hidden
                >
                  ›
                </span>
                Генплан РП
              </th>
            </tr>
          </thead>
        </table>
      </div>

      {generalDesignExpanded && (
        <>
          <GeneralDesignTable
            generalDesignLoading={generalDesignLoading}
            generalDesignError={generalDesignError}
            generalDesignRows={generalDesignRows}
            expandedGeneralProjects={expandedGeneralProjects}
            generalUsersByContract={generalUsersByContract}
            loadingGeneralUsers={loadingGeneralUsers}
            expandedGeneralUsers={expandedGeneralUsers}
            generalReportsByKey={generalReportsByKey}
            loadingGeneralReports={loadingGeneralReports}
            toggleGeneralProject={toggleGeneralProject}
            toggleGeneralUser={toggleGeneralUser}
            savingId={savingId}
            setSavingId={setSavingId}
            setConfirmModal={setConfirmModal}
            setCommentModal={setCommentModal}
            updateGeneralDesignCorrection={updateGeneralDesignCorrection}
            setGeneralReportsByKey={setGeneralReportsByKey}
            formatMoney={formatMoney}
            month={month}
            year={year}
            approvalBySectionId={approvalBySectionId}
            isSectionOnApproval={isSectionOnApproval}
            canDeleteApprovalForSection={canDeleteApprovalForSection}
            deleteApprovalForSection={deleteApprovalForSection}
            deletingApproval={deletingApproval}
            sectionTotalsState={sectionTotalsState}
            isReadOnly={isReadOnly}
          />

          {!isReadOnly && (
            <div className="pp-actions">
              <button
                type="button"
                className="pp-btn pp-btn-primary"
                onClick={openSendApprovalModal}
                disabled={
                  generalDesignLoading ||
                  generalDesignRows.length === 0 ||
                  sectionsWithoutApprovalCount === 0
                }
                title={
                  sectionsWithoutApprovalCount === 0 && generalDesignRows.length > 0
                    ? "Все разделы уже отправлены на согласование"
                    : undefined
                }
              >
                Отправить на согласование
              </button>
            </div>
          )}
        </>
      )}

      {sendApprovalModalOpen && (
        <div
          className="pp-modal-overlay"
          onClick={() => !sendingApproval && setSendApprovalModalOpen(false)}
          role="dialog"
          aria-modal="true"
          aria-labelledby="pp-gp-send-approval-title"
        >
          <div
            className="pp-modal pp-send-approval-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div id="pp-gp-send-approval-title" className="pp-modal-title">
              Отправка Генплан РП на согласование
            </div>
            <div className="pp-modal-body">
              <label className="pp-modal-label">Раздел проекта:</label>
              <Select
                styles={{
                  ...selectStyles,
                  menuPortal: (base) => ({ ...base, zIndex: 2000 }),
                  menu: (base) => ({ ...base, zIndex: 2000 }),
                }}
                options={sectionOptions}
                value={selectedSection}
                onChange={setSelectedSection}
                placeholder="Выберите раздел проекта"
                isClearable
                className="pp-select"
                menuPortalTarget={document.body}
                menuPosition="fixed"
              />

              <label className="pp-modal-label pp-modal-label--mt">
                Кому отправить на согласование:
              </label>
              <Select
                styles={{
                  ...selectStyles,
                  menuPortal: (base) => ({ ...base, zIndex: 2000 }),
                  menu: (base) => ({ ...base, zIndex: 2000 }),
                }}
                options={approverOptions}
                value={selectedApprovers}
                onChange={(opts) => setSelectedApprovers(opts || [])}
                placeholder="Выберите согласующих"
                isMulti
                className="pp-select"
                menuPortalTarget={document.body}
                menuPosition="fixed"
              />
            </div>
            <div className="pp-modal-actions">
              <button
                type="button"
                className="pp-btn pp-btn-secondary"
                onClick={() => setSendApprovalModalOpen(false)}
                disabled={sendingApproval}
              >
                Отмена
              </button>
              <button
                type="button"
                className="pp-btn pp-btn-primary"
                onClick={submitSendApproval}
                disabled={sendingApproval}
              >
                {sendingApproval ? "Отправка..." : "Отправить"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default GeneralDesignSection;
